import React, { useState, useEffect } from 'react';
import { Button, Modal } from '../../../shared/components';
import { 
  Upload,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
  Calendar
} from 'lucide-react';
import { eventService } from '../../../shared/services';
import { filterSelectableEvents } from '../../../shared/utils/eventStatus';

/**
 * Modal para importar Alumnos desde archivo Excel
 * Permite seleccionar archivo XLS y evento para asignar códigos de acceso
 */
const StudentImportModal = ({ 
  isOpen, 
  onClose, 
  onImport
}) => {
  // Estados del formulario
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Estados para eventos
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Cargar eventos al abrir el modal
  useEffect(() => {
    if (isOpen) {
      loadEvents();
    }
  }, [isOpen]);

  // Limpiar formulario al cerrar
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Cargar lista de eventos
  const loadEvents = async () => {
    try {
      setEventsLoading(true);
      const eventsData = await eventService.getEvents();
      setEvents(filterSelectableEvents(eventsData));
    } catch (error) {
      console.error('Error al cargar eventos:', error);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  // Limpiar formulario
  const resetForm = () => {
    setSelectedFile(null);
    setSelectedEventId('');
    setErrors({});
    setDragOver(false);
  };

  // Validar archivo
  const validateFile = (file) => {
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return 'Solo se permiten archivos Excel (.xls, .xlsx) o CSV';
    }

    if (file.size > maxSize) {
      return 'El archivo no puede exceder 5MB';
    }

    return null;
  };

  // Manejar selección de archivo
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        setErrors({ file: error });
        return;
      }
      setSelectedFile(file);
      setErrors({ ...errors, file: '' });
    }
  };

  // Manejar drag & drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        setErrors({ file: error });
        return;
      }
      setSelectedFile(file);
      setErrors({ ...errors, file: '' });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  // Manejar cambio de evento
  const handleEventChange = (e) => {
    setSelectedEventId(e.target.value);
    if (errors.event) {
      setErrors({ ...errors, event: '' });
    }
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    if (!selectedFile) {
      newErrors.file = 'Debe seleccionar un archivo';
    }

    if (!selectedEventId) {
      newErrors.event = 'Debe seleccionar un evento';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('eventId', selectedEventId);

      await onImport(formData);
      resetForm();
    } catch (error) {
      console.error('Error en importación:', error);
    } finally {
      setLoading(false);
    }
  };

  // Formatear fecha del evento
  const formatEventDisplay = (event) => {
    if (!event.date) return event.title;
    const date = new Date(event.date);
    const dateStr = date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const timeStr = date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return `${event.title} - ${dateStr} ${timeStr}`;
  };

  const templateUrl = `${process.env.PUBLIC_URL || ''}/templates/students_template.xlsx`;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="lg"
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Upload className="w-5 h-5 text-green-600" />
            Importar Alumnos desde Excel
          </h2>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="space-y-6">
          {/* Información del proceso */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-900 mb-2">
                  Instrucciones de Importación
                </h3>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• El archivo debe contener las columnas: <strong>COD, APELLIDO Y NOMBRES, TEL CELULAR, CURSO, DESCRIPCION</strong></li>
                  <li>• Cada fila creará un nuevo Alumno para el evento seleccionado</li>
                  <li>• Se generarán códigos de acceso únicos globalmente para cada Alumno</li>
                  <li>• El teléfono es obligatorio para poder enviar notificaciones</li>
                  <li>• No se verificarán duplicados - cada fila es un Alumno único</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Selección de evento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Evento de Destino *
            </label>
            <select
              value={selectedEventId}
              onChange={handleEventChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white ${
                errors.event ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={loading || eventsLoading}
            >
              <option value="">Seleccione un evento</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {formatEventDisplay(event)}
                </option>
              ))}
            </select>
            {errors.event && (
              <p className="mt-1 text-sm text-red-600">{errors.event}</p>
            )}
          </div>

          {/* Área de carga de archivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Archivo Excel *
            </label>
            
            <div
              className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragOver 
                  ? 'border-blue-400 bg-blue-50' 
                  : errors.file
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <input
                type="file"
                onChange={handleFileSelect}
                accept=".xls,.xlsx,.csv"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={loading}
              />
              
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-1">
                    Arrastra tu archivo aquí o haz clic para seleccionar
                  </p>
                  <p className="text-xs text-gray-500">
                    Formatos soportados: .xls, .xlsx, .csv (máx. 5MB)
                  </p>
                </div>
              )}
            </div>
            
            {errors.file && (
              <p className="mt-1 text-sm text-red-600">{errors.file}</p>
            )}
          </div>

          {/* Botón de descarga de template (archivo estático en /public/templates) */}
          <div className="border-t border-gray-200 pt-4">
            <a
              href={templateUrl}
              download
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border rounded-md text-purple-600 border-purple-300 hover:bg-purple-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Descargar Template de Excel
            </a>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Usa este template para asegurar el formato correcto de tu archivo
            </p>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !selectedFile || !selectedEventId}
              className="bg-green-600 hover:bg-green-700 text-white min-w-[120px]"
            >
              {loading ? (
                <>
                  <Upload className="w-4 h-4 mr-2 animate-pulse" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Alumnos
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default StudentImportModal;